<script setup lang="ts">
import { ListCategoryProps } from "./ListCategory.props"
import { getMultiples } from "./Utils"
import { sm } from "~/utils/breakpoints"
import { useRoute } from "vue-router"
import { chunk } from "~/utils/arrays"
import isEqual from "lodash/isEqual"
import { viewItemListMethod } from "~/utils/analytics/viewItemList"

const props = defineProps<ListCategoryProps>()
const emit = defineEmits<{ (e: "onLoadMore"): void }>()

const route = useRoute()

// Count how many available rows of products there are for displaying Banners
const visibleRows = computed(() => {
  const productsNb = props.productsList.length
  return sm.value ? productsNb - 17 : Math.ceil(productsNb / 4) - 4
})

// Count how many times to show the Banners, based on multiples array
const rowsToPopulateWithBanners = computed(() =>
  sm.value
    ? getMultiples(visibleRows.value, [4])
    : getMultiples(visibleRows.value, [2])
)

const bannersToShow = computed(() => {
  if (!props.bannerList) return []
  return props.bannerList.slice(0, rowsToPopulateWithBanners.value.length)
})

// Pattern for spreading Banners on grid's rows
const spreadBanners = (index: number) => {
  return {
    "grid-row-start": sm.value ? index * 5 + 17 : index * 2 + 5
  }
}

const productAreLoading = ref(false)
const handleLoadMore = () => {
  productAreLoading.value = true
  setTimeout(() => {
    productAreLoading.value = false
  }, 1000)
  emit("onLoadMore")
}

/**
 * Computes the 'showMoreLink' based on the current route parameters.
 * @returns {string} The resolved full path for 'showMoreLink'.
 */
const showMoreLink = computed(() => {
  const { page, ...rest } = route.params
  const newPage = route.params.page ? Number(route.params.page) + 1 : 2
  const newParams = {
    page: newPage,
    ...rest
  }

  return router.resolve({
    ...route,
    params: newParams
  }).fullPath
})

/**
 * Computes the 'showMoreLinkWithoutQueryPage' based on the current route query parameters.
 * @returns {string} The resolved full path for 'showMoreLinkWithoutQueryPage'.
 */
const showMoreLinkWithoutQueryPage = computed(() => {
  const { page, ...rest } = route.query
  const newPage = page ? Number(page) + 1 : 2

  return router.resolve({
    path: `${route.path}/page/${newPage}`,
    query: rest
  }).fullPath
})

/**
 * Computes the 'showMoreFirstLinkWithoutQueryPage' without considering the current route parameters.
 * @returns {string} The resolved full path for 'showMoreFirstLinkWithoutQueryPage'.
 */
const showMoreFirstLinkWithoutQueryPage = computed(() => {
  const { page, ...rest } = route.query
  return router.resolve({
    path: `${route.path}/page/2`,
    query: rest
  }).fullPath
})

/**
 * Manages navigation based on route parameters and query.
 * @returns {string} The appropriate path based on the route information.
 */
const manageNavigationShowMore = () => {
  return route.params.page
    ? showMoreLink.value
    : route.fullPath.endsWith("/")
    ? `${route.fullPath.slice(0, -1)}/page/2`
    : route.query.page
    ? showMoreLinkWithoutQueryPage.value
    : showMoreFirstLinkWithoutQueryPage.value
}

const GA_MOBILE_PLP_LENGTH = 2
const GA_DESKTOP_PLP_LENGTH = 4

const chunkedProductList = ref([
  ...chunk(
    props?.productsList,
    sm.value ? GA_MOBILE_PLP_LENGTH : GA_DESKTOP_PLP_LENGTH
  )
])

// As for 2172, with this watch, every time any filter, order or "Load more" gets executed, it will cause the re-render of the ga chunks
watch(
  () => props?.productsList,
  (newValue, oldValue) => {
    if (isEqual(newValue, oldValue)) return // When the before and after objects are the same, then there's no need to re-render the component

    chunkedProductList.value = [
      ...chunk(
        props?.productsList,
        sm.value ? GA_MOBILE_PLP_LENGTH : GA_DESKTOP_PLP_LENGTH
      )
    ]
  }
)

watch(sm, () => {
  if (sm.value) {
    chunkedProductList.value = [
      ...chunk(props?.productsList, GA_MOBILE_PLP_LENGTH)
    ]
  } else {
    chunkedProductList.value = [
      ...chunk(props?.productsList, GA_DESKTOP_PLP_LENGTH)
    ]
  }
})

const method = props?.isSearch
  ? viewItemListMethod.search
  : viewItemListMethod.standard
</script>

<template>
  <div class="list-category grid-standard bg-white md:pb-6">
    <div
      class="
        grid-container grid-products
        col-span-full
        gap-4
        py-4
        md:gap-6
        md:py-6
      "
    >
      <!-- Banners -->
      <ProductBlocksBrandBlockSmall
        v-if="
          route.query?.q == undefined &&
          route.query?.range == undefined &&
          route.query?.refinementList == undefined &&
          route.query?.toggle == undefined
        "
        v-for="(banner, index) in bannersToShow"
        v-bind="banner"
        class="negative-margined col-span-full col-start-1"
        :style="spreadBanners(index)"
        :key="`banner-${banner.card?.productCode}`"
      />

      <template v-for="(chunkedProduct, i) in chunkedProductList" :key="i">
        <template v-for="(product, j) in chunkedProduct" :key="j">
          <div
            :class="`grid-products__card col-span-full w-auto md:col-span-3`"
          >
            <!-- 
              As for TF-2172, the idea to execute the event every list of 4 products is to:
                1. Divide the products in chunks of 4 (2 on mobile), so we have an array of arrays with 4 items (we know its 4 in desktop and 2 in mobile and that the list won't change based on the viewport)
                2. That chunked array must update every time the product list updates (so it doesnt break any filter, order or "Load more")
                3. We link the GA4 execution to the FIRST PRODUCT of that list (of 4 products), so whenever that gets seen in the viewport, we execute that event BUT with the list of 4 in the payload (the "chunkedProduct")
                  that also means that the remaining 3 products MUST NOT have linked up the GA (thats why we use the "disable" below)
            -->

            <GAItem
              :id="`category-${$route.params.category}--${i}`"
              :item="chunkedProduct"
              :disable="j !== 0"
            >
              <GAViewItemList reRender :id="listId" :name="listName">
                <ProductTileOverallResults
                  v-bind="product"
                  class="h-full w-auto"
                  listing-layout
                  :method="method"
                  :itemId="listId"
                  :itemList="listName"
                />
              </GAViewItemList>
            </GAItem>
          </div>
        </template>
      </template>
    </div>

    <AlgoliaPagination
      :max-pages="props.maxPages"
      class="
        col-span-4 col-start-1
        my-2
        md:col-span-8 md:col-start-3
        lg:col-start-3
      "
    />
  </div>
</template>
